/**
 * Advanced Retry Handler with Comprehensive Error Management
 */

// Import the debug utility
import debug from '../../../utils/debug'; // Importing the debug utility

class RetryHandler {
  /**
   * @param {Object} options - Configuration options for retry mechanism
   */
  constructor(options = {}) {
    this.options = {
      maxAttempts: 3,
      initialDelay: 1000,     // 1 second initial delay
      maxDelay: 10000,        // 10 seconds max delay
      backoffFactor: 2,       // Exponential backoff factor
      jitter: 0.2,            // 20% jitter to prevent thundering herd
      retryableErrors: [
        // Network and system-level errors
        'NETWORK_ERROR',
        'UNAVAILABLE',
        'INTERNAL_ERROR',
        'DEADLINE_EXCEEDED',
        'resource-exhausted',
        'unavailable',
        'internal',
        'deadline-exceeded',
        
        // Firebase-specific retriable errors
        'connection-error',
        'timeout',
        'aborted'
      ],
      ...options
    };

    // Log initialization with configuration
    debug.log('RetryHandler', 'Initialized retry handler', {
      maxAttempts: this.options.maxAttempts,
      initialDelay: this.options.initialDelay,
      maxDelay: this.options.maxDelay,
      backoffFactor: this.options.backoffFactor,
      jitter: this.options.jitter
    });
  }

  /**
   * Execute operation with advanced retry logic
   * @param {Function} operation - Async operation to execute
   * @param {Object} [context] - Optional context for logging
   * @returns {Promise<any>} Result of operation
   */
  async execute(operation, context = {}) {
    let lastError;
    let delay = this.options.initialDelay;

    debug.firebase('RetryHandler', 'Starting operation execution', { 
      maxAttempts: this.options.maxAttempts,
      context
    });

    for (let attempt = 1; attempt <= this.options.maxAttempts; attempt++) {
      try {
        // Execute the operation
        debug.log('RetryHandler', `Attempt ${attempt} started`, { context });
        const result = await operation();
        debug.log('RetryHandler', `Operation succeeded on attempt ${attempt}`, { context });
        return result;
      } catch (error) {
        lastError = error;
        
        // Check if error is retryable
        if (!this.isRetryable(error)) {
          // Log non-retryable error
          debug.error('RetryHandler', `Non-retryable error on attempt ${attempt}`, {
            error: error.message,
            errorCode: error.code,
            stack: error.stack,
            context
          });
          throw error;
        }

        // Last attempt check
        if (attempt === this.options.maxAttempts) {
          debug.error('RetryHandler', `Operation failed after ${attempt} attempts`, {
            lastError: error.message,
            errorCode: error.code,
            context,
            totalAttempts: attempt
          });
          throw new Error(`Operation failed after ${attempt} attempts. Last error: ${error.message}`);
        }

        // Log retry attempt with context
        debug.log('RetryHandler', `Attempt ${attempt} failed. Retrying in ${delay}ms`, {
          error: error.message,
          errorCode: error.code,
          context,
          nextAttempt: attempt + 1,
          delayMs: delay
        });

        // Calculate delay with jitter to prevent synchronized retries
        const jitteredDelay = this.applyJitter(delay);
        await this.sleep(jitteredDelay);

        // Exponential backoff with cap
        delay = Math.min(
          delay * this.options.backoffFactor,
          this.options.maxDelay
        );
      }
    }

    debug.error('RetryHandler', 'All retry attempts failed', {
      error: lastError?.message,
      context
    });
    throw lastError;
  }

  /**
   * Apply jitter to delay to prevent synchronized retries
   * @param {number} delay - Original delay
   * @returns {number} Jittered delay
   */
  applyJitter(delay) {
    const jitter = this.options.jitter;
    const min = delay * (1 - jitter);
    const max = delay * (1 + jitter);
    const jitteredDelay = Math.random() * (max - min) + min;
    
    debug.log('RetryHandler', 'Applied jitter to delay', { 
      originalDelay: delay, 
      jitteredDelay: Math.round(jitteredDelay),
      jitterFactor: this.options.jitter
    });
    
    return jitteredDelay;
  }

  /**
   * Check if an error is retryable
   * @param {Error} error - The error to check
   * @returns {boolean} True if error is retryable
   */
  isRetryable(error) {
    const retryable = this.options.retryableErrors.some(code => 
      error.code?.includes(code) || 
      error.message?.includes(code)
    );
    
    debug.log('RetryHandler', `Error retryability check: ${retryable ? 'retryable' : 'non-retryable'}`, {
      errorCode: error.code,
      errorMessage: error.message,
      retryable
    });
    
    return retryable;
  }

  /**
   * Sleep helper with Promise
   * @param {number} ms - Milliseconds to sleep
   * @returns {Promise<void>}
   */
  sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  /**
   * Get statistics about retry operations
   * @param {string} [userUID] - Optional user ID for filtering statistics
   * @returns {Object} Statistics about retry operations
   */
  getStats(userUID) {
    // This is a stub for future implementation
    // Could track success/failure rates, average retry attempts, etc.
    debug.log('RetryHandler', 'Retrieved statistics', { userUID });
    
    return {
      retriesPerformed: 0,  // To be implemented with actual tracking
      successRate: 0,       // To be implemented with actual tracking
      averageAttempts: 0,   // To be implemented with actual tracking
      userUID: userUID || 'all'
    };
  }
}

// Export configurable retry handler
export const retryHandler = new RetryHandler();
export const createRetryHandler = (options) => new RetryHandler(options);
